// Native purchase conditions belong to the canonical form; blocks only project them.
window.AplovioProductPurchase = class {
  constructor(owner) {
    this.owner = owner;
    this.form = owner.nativeForm;
    this.details = this.form.querySelector('[data-purchase-details]');
    this.quantity = this.form.elements.namedItem('quantity');
    this.lastAutomaticQuantity=this.quantity.value;
    this.plan = this.form.querySelector('[data-purchase-plan]');
    this.cache = new WeakMap();
    const options = {signal:owner.abort.signal};
    this.form.addEventListener('input', event => {
      if(event.target===this.quantity){this.quantityDirty=true;owner.updatePurchase(owner.variant.selectedOptions[0]);}
    }, options);
    this.plan?.addEventListener('change', () => owner.updatePurchase(owner.variant.selectedOptions[0]), options);
    this.form.addEventListener('change',event=>{if(event.target!==this.plan && event.target.name==='selling_plan')owner.updatePurchase(owner.variant.selectedOptions[0]);},options);
    this.planObserver=new MutationObserver(records=>{
      const planNode=node=>node.nodeType===1 && node!==this.plan && !this.details?.contains(node) && (node.matches('[name="selling_plan"]') || node.querySelector('[name="selling_plan"]'));
      if(records.some(record=>record.type==='attributes'?planNode(record.target):[...record.addedNodes,...record.removedNodes].some(planNode)))owner.updatePurchase(owner.variant.selectedOptions[0]);
    });
    this.planObserver.observe(document.body,{childList:true,subtree:true,attributes:true,attributeFilter:['name','form','disabled']});
    document.addEventListener('aplovio:cart-updated', event => {
      const cart=event.target;
      if(!cart.matches?.('aplovio-cart'))return;
      const rows=[...cart.querySelectorAll('[data-cart-key]')];
      if(rows.some(row=>!/^\d+$/.test(row.dataset.cartQuantity||'') || !/^\d+$/.test(row.dataset.cartVariant||'')))return;
      if(rows.reduce((sum,row)=>sum+Number(row.dataset.cartQuantity),0)!==event.detail.count)return;
      this.cartCounts=new Map();
      for(const row of rows)this.cartCounts.set(row.dataset.cartVariant,(this.cartCounts.get(row.dataset.cartVariant)||0)+Number(row.dataset.cartQuantity));
      owner.updatePurchase(owner.variant.selectedOptions[0]);
    }, options);
    this.recipient=this.form.querySelector('[data-gift-recipient]');
    this.setupPlanEditor(options);
  }

  setupPlanEditor(options) {
    this.planDialog=this.details?.querySelector('.ap-plan-editor');
    this.planLayer=this.planDialog?.closest('aplovio-layer');
    if(!this.planLayer || !window.AplovioLayers)return;
    this.planTrigger=this.details.querySelector('[data-plan-open]');
    this.planTrigger.hidden=false;
    this.planTrigger.setAttribute('aria-controls',this.planDialog.id);
    this.planTrigger.setAttribute('aria-expanded','false');
    this.details.querySelector('[data-native-plan-picker]').hidden=true;
    this.planTrigger.addEventListener('click',()=>this.planLayer.open(this.planTrigger),options);
    this.planLayer.addEventListener('aplovio:layer-open',event=>{
      if(event.target!==this.planDialog)return;
      this.planContext=this.owner.variant.value;
      this.planDraft=this.plan.value;
      this.renderPlanEditor();
      this.planTrigger.setAttribute('aria-expanded','true');
    },options);
    this.planLayer.addEventListener('aplovio:layer-close',event=>{
      if(event.target!==this.planDialog)return;
      this.planDraft=null;
      this.planTrigger.setAttribute('aria-expanded','false');
      if(this.owner.isConnected)this.owner.updatePurchase(this.owner.variant.selectedOptions[0]);
    },options);
    this.planDialog.addEventListener('change',event=>{
      if(!event.target.matches('[data-plan-choice]'))return;
      this.planDraft=event.target.value;
      this.renderPlanPreview();
    },options);
    this.planDialog.querySelector('[data-plan-apply]').addEventListener('click',()=>this.applyPlan(),options);
    this.form.addEventListener('submit',event=>{
      if(this.planDialog.open){
        event.preventDefault();
        this.applyPlan();
      }
    },{...options,capture:true});
  }

  planLines(plan) {
    if(!plan)return [];
    const strings=this.details.dataset;
    const lines=[];
    if(plan.description)lines.push(plan.description);
    if(plan.adjustments.length>1){
      const [first,next]=plan.adjustments;
      if(first.orders)lines.push(strings.introTerms.replace('[price]',first.price).replace('[count]',String(first.orders)).replace('[next]',next.price));
      else lines.push(`${strings.initialPrice}: ${first.price} · ${strings.afterwards}: ${next.price}`);
    }
    if(plan.deliveryPrice!==plan.price)lines.push(`${strings.perDelivery}: ${plan.deliveryPrice}`);
    if(plan.checkoutCharge!==plan.price || plan.remainingCharge)lines.push(`${strings.dueToday}: ${plan.checkoutCharge}`);
    if(plan.remainingCharge)lines.push(`${strings.remaining}: ${plan.remainingCharge}`);
    return lines;
  }

  renderPlanEditor() {
    const option=this.owner.variant.selectedOptions[0];
    const data=this.current;
    const choices=data?.requiresPlan?[]:[{id:'',name:this.details.dataset.oneTime,price:option?.dataset.price||''}];
    choices.push(...(data?.plans||[]));
    this.planDialog.querySelector('[data-plan-choices]').replaceChildren(...choices.map(choice=>{
      const label=document.createElement('label');
      const input=document.createElement('input');
      const name=document.createElement('span');
      const price=document.createElement('span');
      label.className='ap-plan-choice';
      input.type='radio';
      input.name=this.form.id+'-plan-choice';
      input.setAttribute('form','');
      input.dataset.planChoice='';
      input.value=choice.id;
      input.checked=choice.id===this.planDraft;
      name.textContent=choice.name;
      price.textContent=choice.price;
      price.className='ap-plan-choice__price';
      label.append(input,name,price);
      return label;
    }));
    this.renderPlanPreview();
  }

  renderPlanPreview() {
    const choice=this.current?.plans.find(plan=>plan.id===this.planDraft);
    this.setLines(this.planDialog.querySelector('[data-plan-preview]'),this.planLines(choice));
    const contextUnavailable=this.planContext!==this.owner.variant.value || this.owner.optionPending || this.owner.optionFailure || this.externalPlan || this.invalid;
    this.planDialog.querySelector('[data-plan-apply]').disabled=!!contextUnavailable || !!this.current?.requiresPlan && !choice;
  }

  applyPlan() {
    if(!this.planDialog?.open || this.planDialog.dataset.layerPhase==='closing')return;
    if(this.planContext!==this.owner.variant.value || this.externalPlan || this.owner.optionPending || this.owner.optionFailure || this.invalid)return;
    if(this.planDraft && !this.current?.plans.some(plan=>plan.id===this.planDraft) || this.current?.requiresPlan && !this.planDraft)return;
    this.plan.value=this.planDraft;
    this.plan.dispatchEvent(new Event('input',{bubbles:true}));
    this.plan.dispatchEvent(new Event('change',{bubbles:true}));
    this.planLayer.close('plan-applied');
  }

  data(option) {
    if(!option)return null;
    const raw=option.dataset.purchase;
    if(!raw)return {min:1,step:1,max:null,inCart:0,requiresPlan:false,unit:'',breaks:[],plans:[],pickup:[]};
    if(this.cache.get(option)?.raw===raw)return this.cache.get(option).value;
    const data=JSON.parse(raw);
    const text=value=>typeof value==='string' && value.length<=3000;
    if(!Number.isSafeInteger(data.min)||data.min<1||!Number.isSafeInteger(data.step)||data.step<1||data.max!==null&&(!Number.isSafeInteger(data.max)||data.max<0)||!Number.isSafeInteger(data.inCart)||data.inCart<0||typeof data.requiresPlan!=='boolean'||!text(data.unit)||!Array.isArray(data.breaks)||!Array.isArray(data.plans)||!Array.isArray(data.pickup))throw new Error('Invalid native purchase data');
    if(data.breaks.some(row=>!Number.isSafeInteger(row.minimum)||row.minimum<1||!text(row.price)||!text(row.priceWithCurrency)))throw new Error('Invalid native price breaks');
    if(data.plans.some(plan=>!/^\d+$/.test(plan.id)||['name','description','price','priceWithCurrency','comparePrice','comparePriceWithCurrency','deliveryPrice','checkoutCharge','remainingCharge','unit'].some(key=>!text(plan[key]))||!Array.isArray(plan.adjustments)||plan.adjustments.some(row=>!text(row.price)||row.orders!==null&&(!Number.isSafeInteger(row.orders)||row.orders<1))))throw new Error('Invalid native selling plan');
    if(data.pickup.some(place=>typeof place.available!=='boolean'||['name','time','address'].some(key=>!text(place[key]))))throw new Error('Invalid native pickup');
    this.cache.set(option,{raw,value:data});return data;
  }

  update(option) {
    if(!this.details)return;
    try {this.current=this.data(option);this.invalid=false;}
    catch(error){this.current=null;this.invalid=true;if(this.owner.refreshNotice)this.owner.refreshNotice.hidden=false;this.owner.dispatchEvent(new CustomEvent('aplovio:purchase-data-error',{bubbles:true,detail:{reason:error.message}}));}
    const data=this.current,strings=this.details.dataset;
    const external=[...this.form.elements].filter(input=>input!==this.plan && input.name==='selling_plan');
    this.externalPlan=external.length>0;
    this.plan.disabled=this.externalPlan || !option || this.owner.optionPending || this.owner.optionFailure || this.invalid;
    const planSignature=JSON.stringify([option?.value, data?.requiresPlan, data?.plans]);
    if(planSignature!==this.planSignature){
      this.planSignature=planSignature;
      const selection=this.plan.value;
      this.plan.replaceChildren(new Option(data?.requiresPlan?strings.choosePlan:strings.oneTime,''),...(data?.plans||[]).map(plan=>new Option(`${plan.name} — ${plan.price}`,plan.id)));
      this.plan.value=(data?.plans||[]).some(plan=>plan.id===selection)?selection:'';
      this.plan.required=!!data?.requiresPlan;
    }
    if(this.planDialog?.open && (this.externalPlan || this.planContext!==option?.value || this.owner.optionPending || this.owner.optionFailure || this.invalid))this.planLayer.close('purchase-context');
    this.details.querySelector('[data-plan-field]').hidden=!this.planDialog?.open && (this.externalPlan || !data?.plans.length && !data?.requiresPlan);
    const externalSelection=external.filter(input=>!input.disabled && (!['radio','checkbox'].includes(input.type)||input.checked)).map(input=>input.value).filter(Boolean);
    const selectedPlanId=this.externalPlan?(new Set(externalSelection).size===1?externalSelection[0]:''):this.plan.value;
    this.selectedPlan=data?.plans.find(plan=>plan.id===selectedPlanId);
    this.invalidExternalPlan=this.externalPlan && (new Set(externalSelection).size>1 || !!selectedPlanId && !this.selectedPlan);
    const planDetails=this.details.querySelector('[data-plan-details]');
    this.setLines(planDetails,this.planLines(this.selectedPlan));
    if(this.planTrigger){
      this.planTrigger.disabled=!option || !!this.owner.optionPending || !!this.owner.optionFailure || this.invalid;
      this.details.querySelector('[data-plan-summary]').textContent=this.selectedPlan?.name || (data?.requiresPlan?strings.choosePlan:strings.oneTime);
    }
    if(this.planDialog?.open)this.renderPlanPreview();
    const inCart=option?(this.cartCounts?this.cartCounts.get(option.value)||0:data?.inCart||0):0;
    if(this.quantity.value!==this.lastAutomaticQuantity)this.quantityDirty=true;
    const minimum=data?(inCart>=data.min?data.step:data.min-inCart):1;
    const maximum=data?.max===null || !data?null:Math.max(0,data.max-inCart);
    this.limitReached=maximum!==null && maximum<minimum;
    this.requiresQuantity=!!data && (data.min>1 || data.step>1 || data.max!==null);
    this.quantity.min=String(minimum);this.quantity.step=String(data?.step||1);
    if(maximum===null)this.quantity.removeAttribute('max');else this.quantity.max=String(maximum);
    this.quantity.setAttribute('aria-describedby',this.details.querySelector('[data-quantity-rules]').id);
    const quantitySignature=JSON.stringify([option?.value,minimum,maximum,data?.step]);
    if(quantitySignature!==this.quantitySignature){
      this.quantitySignature=quantitySignature;
      if(!this.quantityDirty){this.quantity.value=String(minimum);this.lastAutomaticQuantity=this.quantity.value;}
    }
    const rules=this.details.querySelector('[data-quantity-rules]');
    rules.textContent=this.limitReached?strings.limitReached:this.requiresQuantity?[`${strings.minimum}: ${minimum}`,`${strings.increment}: ${data.step}`,maximum!==null?`${strings.maximum}: ${maximum}`:'',`${strings.inCart}: ${inCart}`].filter(Boolean).join(' · '):'';
    rules.hidden=!rules.textContent;
    const nativeQuantity=this.form.querySelector('[data-native-quantity]');
    if(nativeQuantity && this.requiresQuantity && this.owner.dataset.quantityLayout==='hidden')nativeQuantity.hidden=false;
    const breaks=this.details.querySelector('[data-volume-prices]');
    breaks.hidden=!data?.breaks.length || !!this.selectedPlan;
    const breaksSignature=JSON.stringify([option?.value,data?.breaks]);
    if(breaksSignature!==this.breaksSignature){
      this.breaksSignature=breaksSignature;
      breaks.querySelector('tbody').replaceChildren(...(data?.breaks||[]).map(row=>{const tr=document.createElement('tr'),amount=document.createElement('td'),price=document.createElement('td');amount.textContent=row.minimum+'+';price.textContent=row.price;tr.append(amount,price);return tr;}));
    }
    const amount=this.quantity.valueAsNumber+inCart;
    this.selectedBreak=!this.selectedPlan && Number.isSafeInteger(amount)?[...(data?.breaks||[])].sort((a,b)=>b.minimum-a.minimum).find(row=>amount>=row.minimum):null;
    this.unit=this.selectedPlan?.unit ?? this.selectedBreak?.unit ?? data?.unit ?? '';
    const pickup=this.details.querySelector('[data-pickup]');pickup.hidden=!!this.recipient || !data?.pickup.length;
    const pickupSignature=JSON.stringify(data?.pickup);
    if(pickupSignature!==this.pickupSignature){
      this.pickupSignature=pickupSignature;
      pickup.querySelector('ul').replaceChildren(...(data?.pickup||[]).map(place=>{const li=document.createElement('li'),name=document.createElement('strong'),status=document.createElement('p'),address=document.createElement('p');name.textContent=place.name;status.textContent=place.available?`${strings.pickupReady} · ${place.time}`:strings.pickupUnavailable;address.textContent=place.address;li.append(name,status,address);return li;}));
    }
  }

  setLines(host,lines) {
    const signature=JSON.stringify(lines);if(host.dataset.signature===signature)return;
    host.dataset.signature=signature;
    host.replaceChildren(...lines.map(line=>{const paragraph=document.createElement('p');paragraph.textContent=line;return paragraph;}));
  }

  destroy(){this.planObserver?.disconnect();}
  canPurchase() {return !this.invalid && !this.invalidExternalPlan && !this.limitReached && (!this.current?.requiresPlan || !!this.selectedPlan);}
  priceOption(option) {
    const pricing=this.selectedPlan || this.selectedBreak;
    if(!option || !pricing)return option;
    const display=option.cloneNode(true);
    for(const field of ['price','priceWithCurrency','comparePrice','comparePriceWithCurrency','discount'])display.dataset[field]=pricing[field] || '';
    // The 30-day lowest price describes the variant's own price, not a plan or quantity price.
    display.dataset.lowestPrice='';
    return display;
  }
};
